import { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import UploadFinancials from './UploadFinancials';
import SettingsPanel from './SettingsPanel';
import WeeklyDashboard from './WeeklyDashboard';
import TabDashboard from './TabDashboard';

const API = process.env.REACT_APP_API_URL || '';

function fmt$(n) {
  if (n == null) return '—';
  if (Math.abs(n) >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + Math.round(n).toLocaleString();
}
function fmtPct(n, dec = 1) { return n == null ? '—' : (n * 100).toFixed(dec) + '%'; }

// ── Tab bar ──────────────────────────────────────────────────
function TabBar({ activeTab, setActiveTab, activeDivision, setActiveDivision, divisions }) {
  const hasDivisions = divisions && divisions.length > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 10 }}>
      <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 9, padding: 3, gap: 2 }}>
        {[{ key: 'strategy', label: 'Strategy' }, { key: 'operations', label: 'Operations' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ fontSize: 12, fontWeight: 500, padding: '6px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: activeTab === tab.key ? '#fff' : 'transparent', color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: activeTab === tab.key ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>
      {hasDivisions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>View:</span>
          <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 7, padding: 2, gap: 2 }}>
            <button onClick={() => setActiveDivision('total')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: activeDivision === 'total' ? '#fff' : 'transparent', color: activeDivision === 'total' ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: activeDivision === 'total' ? 500 : 400, boxShadow: activeDivision === 'total' ? 'var(--shadow-sm)' : 'none' }}>Total</button>
            {divisions.map(div => (
              <button key={div.name} onClick={() => setActiveDivision(div.name)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: activeDivision === div.name ? '#fff' : 'transparent', color: activeDivision === div.name ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: activeDivision === div.name ? 500 : 400, boxShadow: activeDivision === div.name ? 'var(--shadow-sm)' : 'none', whiteSpace: 'nowrap' }}>{div.name}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── KPI Card ─────────────────────────────────────────────────
function KpiCard({ label, value, yoy, yoyLabel, benchmarkLabel }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {yoyLabel && <div style={{ fontSize: 11, color: yoy > 0 ? 'var(--green)' : yoy < 0 ? 'var(--red)' : 'var(--text-muted)' }}>{yoy != null && yoy !== 0 ? (yoy > 0 ? '▲ ' : '▼ ') : ''}{yoyLabel}</div>}
        {benchmarkLabel && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{benchmarkLabel}</div>}
      </div>
    </div>
  );
}

// ── Valuation cards ──────────────────────────────────────────
function ValuationCards({ kpis, multiples, settings, customerSales }) {
  if (!multiples) return null;
  const avgEBITDA = (kpis?.ebitda || 0) || ((kpis?.ttmRevenue || 0) * (kpis?.ebitdaMarginPct || 0));
  if (!avgEBITDA) return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div className="section-label">Implied valuation</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        {['TTM EBITDA', `${multiples.industry || ''} multiple`, 'Implied enterprise value'].map((label, i) => (
          <div key={i} style={{ background: i === 0 ? 'var(--navy)' : i === 2 ? 'var(--green-bg)' : '#fff', borderRadius: 'var(--radius-md)', padding: '16px 18px', border: i === 0 ? 'none' : '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 11, color: i === 0 ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)', marginBottom: 6, fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: i === 0 ? '#fff' : 'var(--text-muted)', letterSpacing: '-0.02em' }}>—</div>
            <div style={{ fontSize: 11, color: i === 0 ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)', marginTop: 5 }}>Upload template to populate</div>
          </div>
        ))}
      </div>
    </div>
  );

  let totalDeduction = 0;
  if (settings?.owner_dependent) totalDeduction += 0.5;
  if (settings?.recurring_revenue_pct != null && settings.recurring_revenue_pct < 25) totalDeduction += 0.5;
  const topPct = customerSales?.[0]?.revenue > 0 && kpis?.ttmRevenue > 0 ? (customerSales[0].revenue / kpis.ttmRevenue) : (kpis?.topCustomerPct || 0);
  if (topPct > 0.25) totalDeduction += 0.5;
  if (settings?.revenue_predictable === false) totalDeduction += 0.5;
  if (settings?.financial_visibility === false) totalDeduction += 0.5;
  if (settings?.capex_pct != null && settings.capex_pct > 2.5) totalDeduction += 0.5;

  const adjMid = Math.max(1, multiples.multiple_mid - totalDeduction);
  const valMid = Math.round(avgEBITDA * adjMid);
  const updatedYear = multiples.updated_at ? new Date(multiples.updated_at).getFullYear() : 2026;

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div className="section-label">Implied valuation</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        <div style={{ background: 'var(--navy)', borderRadius: 'var(--radius-md)', padding: '16px 18px', boxShadow: '0 4px 16px rgba(10,22,40,0.18)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, fontWeight: 500 }}>TTM EBITDA</div>
              <div style={{ fontSize: 26, fontWeight: 600, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{fmt$(avgEBITDA)}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 5 }}>Approx · net income basis</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.35, marginTop: 2 }}>
              <polyline points="2,14 7,8 11,11 18,4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="13,4 18,4 18,9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 'var(--radius-md)', padding: '16px 18px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 500 }}>{multiples.industry} · multiple</div>
              <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{adjMid.toFixed(1)}x</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>Updated {updatedYear}</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.3, marginTop: 2 }}>
              <rect x="2" y="10" width="4" height="8" rx="1" fill="currentColor"/>
              <rect x="8" y="6" width="4" height="12" rx="1" fill="currentColor"/>
              <rect x="14" y="2" width="4" height="16" rx="1" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div style={{ background: 'var(--green-bg)', borderRadius: 'var(--radius-md)', padding: '16px 18px', border: '1px solid rgba(26,122,60,0.15)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 6, fontWeight: 500 }}>Implied enterprise value</div>
              <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>{fmt$(valMid)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5 }}>Indicative · not a formal valuation</div>
            </div>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.3, marginTop: 2, color: 'var(--green)' }}>
              <path d="M10 2L2 7h16L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
              <rect x="4" y="7" width="2.5" height="9" rx="0.5" fill="currentColor" opacity="0.6"/>
              <rect x="8.75" y="7" width="2.5" height="9" rx="0.5" fill="currentColor" opacity="0.6"/>
              <rect x="13.5" y="7" width="2.5" height="9" rx="0.5" fill="currentColor" opacity="0.6"/>
              <line x1="2" y1="16" x2="18" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Observations ─────────────────────────────────────────────
function ObservationCard({ obs, index }) {
  const types = { positive: { bar: 'var(--green)', num: 'var(--green)', label: 'Strength' }, warning: { bar: 'var(--amber)', num: 'var(--amber)', label: 'Watch' }, action: { bar: 'var(--accent)', num: 'var(--accent)', label: 'Action' } };
  const t = types[obs.type] || types.action;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '1rem 1.1rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: t.bar }} />
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.num, marginBottom: 8 }}>{String(index + 1).padStart(2, '0')} · {t.label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8, lineHeight: 1.4 }}>{obs.title}</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>{obs.body}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '6px 0', borderTop: '1px solid var(--border)', marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{obs.stat}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{obs.statLabel}</span>
      </div>
      <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', width: '100%', textAlign: 'left', fontFamily: 'inherit' }}>{obs.prompt} ↗</button>
    </div>
  );
}

function ObservationsSection({ slug, financialData }) {
  const [observations, setObservations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => { if (financialData) generate(); }, [financialData]);
  async function generate() {
    try {
      setLoading(true); setError(null);
      const res = await fetch(`${API}/api/intel?resource=observations`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, financialData }) });
      const data = await res.json();
      if (data.observations) setObservations(data.observations);
      else setError(data.error || 'Could not generate');
    } catch { setError('Failed to generate'); } finally { setLoading(false); }
  }
  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5L8.5 5.5H13L9.5 8L11 12L7 9.5L3 12L4.5 8L1 5.5H5.5L7 1.5Z" stroke="white" strokeWidth="1.2" fill="none" strokeLinejoin="round"/></svg>
          </div>
          <div><div style={{ fontSize: 13, fontWeight: 600 }}>SCV Observations</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>AI-generated · refreshes on every load</div></div>
        </div>
        {!loading && <button onClick={generate} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit' }}>Regenerate</button>}
      </div>
      {loading && <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Analyzing financial data...</div>}
      {error && <div style={{ background: 'var(--amber-bg)', border: '1px solid rgba(212,130,26,0.25)', borderRadius: 'var(--radius-md)', padding: '1rem', color: 'var(--amber)', fontSize: 12 }}>{error.includes('credit') || error.includes('billing') ? <>⚠ API credits needed — add at <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={{ color: 'var(--amber)' }}>console.anthropic.com</a></> : error}</div>}
      {observations && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12 }}>{observations.map((obs, i) => <ObservationCard key={i} obs={obs} index={i} />)}</div>}
      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-muted)' }}>✦ Generated by Claude · based on uploaded data · not financial advice</div>
    </div>
  );
}

// ── Strategy Dashboard ────────────────────────────────────────
function StrategyDashboard({ slug, user, company, kpis, monthlyData, trailingData, customerSales, divisions, benchmarks, multiples, companySettings, observationsData, activeDivision }) {
  const [chartView, setChartView] = useState('monthly');

  const divData = activeDivision !== 'total' ? divisions?.find(d => d.name === activeDivision) : null;
  const displayKpis = divData ? { ...kpis, ttmRevenue: divData.revenue, grossMarginPct: divData.grossMarginPct } : kpis;
  const displayMonthly = monthlyData;
  const displayTrailing = trailingData;
  const displayCustomers = activeDivision === 'total' ? customerSales : [];
  const chartData = chartView === 'monthly' ? displayMonthly : displayTrailing;

  function bDelta(actual, median, isPct = false, inverse = false) {
    if (actual == null || median == null) return null;
    const diff = actual - median;
    const positive = inverse ? diff < 0 : diff > 0;
    const label = isPct ? `${diff > 0 ? '+' : ''}${(diff * 100).toFixed(1)}pts vs ${(median * 100).toFixed(0)}% median` : `${diff > 0 ? '+' : ''}${fmt$(diff)} vs ${fmt$(median)} median`;
    return { label, positive };
  }

  const revYoy = displayKpis?.revenueGrowthPct != null ? { value: displayKpis.revenueGrowthPct, label: `${displayKpis.revenueGrowthPct > 0 ? '+' : ''}${(displayKpis.revenueGrowthPct * 100).toFixed(1)}% vs prior year` } : null;
  const gmYoy = displayKpis?.grossMarginPct != null && displayKpis?.priorGrossMarginPct != null ? { value: displayKpis.grossMarginPct - displayKpis.priorGrossMarginPct, label: `${((displayKpis.grossMarginPct - displayKpis.priorGrossMarginPct) * 100) > 0 ? '+' : ''}${((displayKpis.grossMarginPct - displayKpis.priorGrossMarginPct) * 100).toFixed(1)}pts vs prior year` } : null;
  const ebYoy = displayKpis?.ebitda != null && displayKpis?.priorEBITDA != null ? { value: displayKpis.ebitda - displayKpis.priorEBITDA, label: `${displayKpis.ebitda - displayKpis.priorEBITDA > 0 ? '+' : ''}${fmt$(displayKpis.ebitda - displayKpis.priorEBITDA)} vs prior year` } : null;
  const gmBench = bDelta(displayKpis?.grossMarginPct, benchmarks?.median_gross_margin, true);
  const ebBench = bDelta(displayKpis?.ebitdaMarginPct, benchmarks?.median_ebitda_margin, true);
  const rpeBench = bDelta(displayKpis?.revPerEmployee, benchmarks?.median_rev_per_employee, false);

  return (
    <div>
      <ValuationCards kpis={displayKpis} multiples={multiples} settings={companySettings} customerSales={displayCustomers} />

      <div style={{ marginBottom: '1.25rem' }}>
        <div className="section-label">Key performance indicators</div>
        {/* Top row — 4 cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0,1fr))', gap: 10, marginBottom: 10 }}>
          <div style={{ gridColumn: 'span 2' }}><KpiCard label="TTM Revenue" value={fmt$(displayKpis?.ttmRevenue)} yoy={revYoy?.value} yoyLabel={revYoy?.label} benchmarkLabel={displayKpis?.priorTTMRevenue ? `Prior year ${fmt$(displayKpis.priorTTMRevenue)}` : null} /></div>
          <div style={{ gridColumn: 'span 2' }}><KpiCard label="Gross Margin" value={fmtPct(displayKpis?.grossMarginPct)} yoy={gmYoy?.value} yoyLabel={gmYoy?.label} benchmarkLabel={benchmarks && gmBench ? `${gmBench.positive ? '▲' : '▼'} ${gmBench.label}` : null} /></div>
          <div style={{ gridColumn: 'span 2' }}><KpiCard label="EBITDA" value={fmt$(displayKpis?.ebitda)} yoy={ebYoy?.value} yoyLabel={ebYoy?.label} benchmarkLabel={benchmarks && ebBench ? `${ebBench.positive ? '▲' : '▼'} ${ebBench.label}` : null} /></div>
          <div style={{ gridColumn: 'span 2' }}><KpiCard label="Revenue / Employee" value={displayKpis?.revPerEmployee ? fmt$(displayKpis.revPerEmployee) : '—'} yoyLabel={displayKpis?.headcount ? `${displayKpis.headcount} employees` : null} benchmarkLabel={benchmarks && rpeBench ? `${rpeBench.positive ? '▲' : '▼'} ${rpeBench.label}` : null} /></div>
        </div>
        {/* Bottom row — 3 cards centered */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0,1fr))', gap: 10 }}>
          <div style={{ gridColumn: '2 / 4' }}><KpiCard label="Cash on Hand" value={displayKpis?.cash > 0 ? fmt$(displayKpis.cash) : '—'} yoyLabel={displayKpis?.cash ? 'From bank statement' : 'Upload bank statement'} /></div>
          <div style={{ gridColumn: '4 / 6' }}><KpiCard label="Accounts Receivable" value={displayKpis?.ar > 0 ? fmt$(displayKpis.ar) : '—'} yoyLabel={displayKpis?.dso ? `${displayKpis.dso.toFixed(1)} days DSO` : 'Connect QBO for live AR'} /></div>
          <div style={{ gridColumn: '6 / 8' }}><KpiCard label="Revenue Growth Y/Y" value={displayKpis?.revenueGrowthPct != null ? `${displayKpis.revenueGrowthPct > 0 ? '+' : ''}${(displayKpis.revenueGrowthPct * 100).toFixed(1)}%` : '—'} yoy={displayKpis?.revenueGrowthPct} yoyLabel={displayKpis?.priorTTMRevenue ? `vs ${fmt$(displayKpis.priorTTMRevenue)} prior year` : null} /></div>
        </div>
      </div>

      <div className="section-card">
        <div className="card-hd">
          <div className="card-title">Revenue, gross profit &amp; EBITDA</div>
          <div className="toggle-group">
            <button className={`tgl ${chartView === 'monthly' ? 'active' : ''}`} onClick={() => setChartView('monthly')}>Monthly</button>
            <button className={`tgl ${chartView === 'trailing' ? 'active' : ''}`} onClick={() => setChartView('trailing')}>Trailing 12M</button>
          </div>
        </div>
        <div className="legend-row">
          <div className="leg"><span className="leg-sq" style={{ background: 'var(--accent)' }}></span>Revenue</div>
          <div className="leg"><span className="leg-sq" style={{ background: 'var(--green)' }}></span>Gross profit</div>
          <div className="leg"><span className="leg-sq" style={{ background: 'var(--navy)' }}></span>EBITDA</div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={fmt$} />
            <Tooltip formatter={(v, n) => [fmt$(v), n === 'revenue' ? 'Revenue' : n === 'grossProfit' ? 'Gross profit' : 'EBITDA']} contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
            <Bar dataKey="revenue" fill="#1e5fd4" radius={[3, 3, 0, 0]} />
            <Bar dataKey="grossProfit" fill="#1a7a3c" radius={[3, 3, 0, 0]} />
            <Bar dataKey="ebitda" fill="#0a1628" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="two-col">
        <div className="section-card">
          <div className="card-title" style={{ marginBottom: '0.75rem' }}>Revenue by customer · TTM</div>
          {displayCustomers.length === 0 ? <p className="empty-msg">Upload customer sales data via the template</p>
            : displayCustomers.slice(0, 8).map((job, i) => (
              <div className="job-row" key={i}>
                <div><div className="job-name">{job.name}</div><div className="job-meta">{fmt$(job.revenue)}{displayKpis?.ttmRevenue > 0 ? ` · ${Math.round((job.revenue / displayKpis.ttmRevenue) * 100)}% of TTM` : ''}</div></div>
              </div>
            ))}
        </div>
        <div className="section-card">
          <div className="card-title" style={{ marginBottom: '0.75rem' }}>Gross margin trend · trailing 6M</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={displayMonthly.slice(-6).map(m => ({ label: m.label, margin: m.revenue > 0 ? Math.round((m.grossProfit / m.revenue) * 100) : 0 }))}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => v + '%'} domain={[0, 'auto']} />
              <Tooltip formatter={v => [v + '%', 'Gross margin']} contentStyle={{ borderRadius: 6, fontSize: 11 }} />
              <Line type="monotone" dataKey="margin" stroke="var(--accent)" strokeWidth={1.5} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
          {divisions && divisions.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>Revenue by division</div>
              {divisions.map((div, i) => {
                const total = divisions.reduce((s, d) => s + d.revenue, 0);
                const pct = total > 0 ? Math.round((div.revenue / total) * 100) : 0;
                return (
                  <div key={i} className="ar-row">
                    <div className="ar-lbl">{div.name}</div>
                    <div className="ar-bar-wrap"><div className="ar-bar" style={{ width: `${pct}%`, background: 'var(--navy)' }} /></div>
                    <div className="ar-amt">{fmt$(div.revenue)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {observationsData && <ObservationsSection slug={slug} financialData={observationsData} />}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────
export default function CompanyDashboard({ slug, user, onLogout }) {
  const [company, setCompany] = useState(null);
  const [kpis, setKpis] = useState({ ttmRevenue: null, grossMarginPct: null, ebitdaMarginPct: null, ebitda: null, cash: null, ar: null, dso: null, headcount: null, revPerEmployee: null, topCustomerPct: null, revenueGrowthPct: null, priorTTMRevenue: null, priorGrossMarginPct: null, priorEBITDA: null, priorEBITDAMarginPct: null, dataSource: null });
  const [monthlyData, setMonthlyData] = useState([]);
  const [trailingData, setTrailingData] = useState([]);
  const [customerSales, setCustomerSales] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [multiples, setMultiples] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [observationsData, setObservationsData] = useState(null);
  const [activeTab, setActiveTab] = useState('strategy');
  const [templateData, setTemplateData] = useState(null);
  const [activeDivision, setActiveDivision] = useState('total');

  useEffect(() => { if (slug) loadAll(); }, [slug]);

  useEffect(() => {
    if (!company?.industry) return;
    Promise.all([
      fetch(`${API}/api/intel?resource=multiples&industry=${encodeURIComponent(company.industry)}`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/intel?resource=benchmarks&industry=${encodeURIComponent(company.industry)}`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/intel?resource=settings&slug=${slug}`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
    ]).then(([mult, bench, settings]) => {
      if (mult?.multiple_mid) setMultiples(mult);
      if (bench?.median_gross_margin) setBenchmarks(bench);
      if (settings) setCompanySettings(settings);
    });
  }, [company?.industry, slug]);

  async function loadAll() {
    try {
      setLoading(true); setError(null);
      const companyRes = await fetch(`${API}/api/companies?slug=${slug}`, { credentials: 'include' });
      if (!companyRes.ok) { setError('Company not found'); return; }
      const companyData = await companyRes.json();
      if (!companyData || companyData.error) { setError(companyData?.error || 'Company not found'); return; }
      setCompany(companyData);
      if (companyData.qb_connected) {
        await loadQBOData(companyData);
      } else {
        await loadManualData();
      }
    } catch (err) {
      console.error('loadAll error:', err);
      setError('Failed to load dashboard: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadQBOData(companyData) {
    try {
      const today = new Date(); const todayStr = today.toISOString().split('T')[0];
      const ttmStart = new Date(today); ttmStart.setFullYear(ttmStart.getFullYear() - 1);
      const ttmStartStr = ttmStart.toISOString().split('T')[0];
      const report = async (type, params = {}) => { const qs = new URLSearchParams({ slug, type, ...params }).toString(); const res = await fetch(`${API}/api/reports?${qs}`, { credentials: 'include' }); return res.ok ? res.json() : null; };
      const payments = await report('payments', { start_date: ttmStartStr, end_date: todayStr });
      const allPayments = payments?.QueryResponse?.Payment || [];
      const months = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const rev = allPayments.filter(p => { const pd = new Date(p.TxnDate); return pd.getFullYear() === d.getFullYear() && pd.getMonth() === d.getMonth(); }).reduce((s, p) => s + parseFloat(p.TotalAmt || 0), 0);
        months.push({ label: d.toLocaleString('default', { month: 'short', year: '2-digit' }), revenue: Math.round(rev), grossProfit: Math.round(rev * 0.3), ebitda: Math.round(rev * 0.1) });
      }
      setMonthlyData(months);
      setTrailingData(months.map((m, i) => { const w = months.slice(Math.max(0, i - 11), i + 1); return { label: m.label, revenue: w.reduce((s, x) => s + x.revenue, 0), grossProfit: w.reduce((s, x) => s + x.grossProfit, 0), ebitda: w.reduce((s, x) => s + x.ebitda, 0) }; }));
      const ttmRev = months.reduce((s, m) => s + m.revenue, 0);
      setKpis(prev => ({ ...prev, ttmRevenue: ttmRev, grossMarginPct: 0.3, ebitdaMarginPct: 0.1, headcount: companyData.employee_count, revPerEmployee: companyData.employee_count > 0 ? ttmRev / companyData.employee_count : null, dataSource: 'qbo' }));
    } catch (err) { console.error('QBO error:', err); }
  }

  async function loadManualData() {
    const res = await fetch(`${API}/api/data?resource=financials&slug=${slug}`, { credentials: 'include' });
    const { financials } = await res.json();
    if (!financials?.length) return;
    const pl = financials.find(f => f.upload_type === 'template' || f.upload_type === 'pl');
    if (!pl?.data) return;
    const d = pl.data;
    // New v3 template — has tabs, hand off to TabDashboard
    if (d.tabs || d.templateVersion === 'scv_v3' || !d.kpis) {
      if (d.tabs?.length > 0) setTemplateData(d);
      return;
    }
    // Legacy format
    const bank = financials.find(f => f.upload_type === 'bank_statement');
    const bal  = financials.find(f => f.upload_type === 'balance_sheet');
    const cash = bank?.data?.endingBalance || bal?.data?.cash || null;
    const kpis = d.kpis || {};
    const kpiData = { ...kpis, cash, ar: kpis.ar || bal?.data?.accountsReceivable || null, dataSource: 'template', grossMarginPct: kpis.grossMarginPct > 1 ? kpis.grossMarginPct / 100 : kpis.grossMarginPct, ebitdaMarginPct: kpis.ebitdaMarginPct > 1 ? kpis.ebitdaMarginPct / 100 : kpis.ebitdaMarginPct };
    setKpis(kpiData);
    setMonthlyData(d.months || []);
    setTrailingData((d.rollingTTM || []).slice(-18));
    setCustomerSales(d.customers || []);
    setDivisions(d.divisions || []);
  }

  if (loading) return <div className="loading-screen"><div className="portal-logo">SCV</div><p>Loading dashboard...</p></div>;
  if (error) return <div className="loading-screen"><p className="error-msg">{error}</p><button onClick={loadAll} className="login-btn" style={{ marginTop: 16 }}>Retry</button></div>;

  return (
    <div className="dash">
      {/* SCV Header bar */}
      <div className="scv-header-bar no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', padding: '12px 16px', borderRadius: 12, background: '#fff', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        {/* Left: SCV branding */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <svg width="48" height="48" viewBox="0 0 100 100" style={{ display: 'block' }}>
            <circle cx="50" cy="50" r="50" fill="white"/>
            <ellipse cx="50" cy="48" rx="44" ry="45" fill="none" stroke="#c0c0c0" strokeWidth="3" opacity="0.7"/>
            <ellipse cx="51" cy="51" rx="42" ry="43" fill="none" stroke="#d0d0d0" strokeWidth="2" opacity="0.5"/>
            <ellipse cx="50" cy="50" rx="40" ry="41" fill="none" stroke="#c9a84c" strokeWidth="2.5" opacity="0.95"/>
            <ellipse cx="49" cy="50" rx="37" ry="38" fill="none" stroke="#c0c0c0" strokeWidth="2" opacity="0.5"/>
            <line x1="52" y1="14" x2="52" y2="86" stroke="#b8b8b8" strokeWidth="1" opacity="0.8"/>
            <line x1="51" y1="14" x2="51" y2="86" stroke="#c0c0c0" strokeWidth="0.6" opacity="0.5"/>
            <line x1="53" y1="14" x2="53" y2="86" stroke="#c0c0c0" strokeWidth="0.6" opacity="0.5"/>
            <line x1="28" y1="38" x2="74" y2="38" stroke="#b8b8b8" strokeWidth="0.8" opacity="0.8"/>
            <line x1="28" y1="40" x2="74" y2="40" stroke="#b8b8b8" strokeWidth="0.8" opacity="0.7"/>
            <line x1="28" y1="42" x2="74" y2="42" stroke="#b0b0b0" strokeWidth="0.7" opacity="0.6"/>
            <line x1="28" y1="44" x2="74" y2="44" stroke="#b0b0b0" strokeWidth="0.6" opacity="0.5"/>
            <line x1="28" y1="46" x2="74" y2="46" stroke="#b0b0b0" strokeWidth="0.5" opacity="0.4"/>
            <line x1="28" y1="48" x2="74" y2="48" stroke="#b0b0b0" strokeWidth="0.5" opacity="0.3"/>
            <rect x="49.5" y="60" width="5" height="8" fill="none" stroke="#c0c0c0" strokeWidth="0.8" opacity="0.6"/>
          </svg>
          <div>
            <div style={{ fontSize: 10, fontWeight: 400, color: '#888', letterSpacing: '0.02em' }}>Seven Circles Ventures</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f1923', letterSpacing: '-0.02em', marginTop: 2 }}>{company?.name || slug}</div>
          </div>
        </div>

        {/* Right: Company name + nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{company?.industry || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {user.role === 'scv_admin' && <button className="btn-outline" onClick={() => { window.history.pushState({}, '', '/'); window.location.reload(); }}>← Portfolio</button>}
            {!company?.qb_connected && user.role === 'scv_admin' && (
              <UploadFinancials slug={slug} onDataUploaded={() => loadAll()} compact />
            )}
            {user.role === 'scv_admin' && (
              <button className="btn-outline" onClick={() => setShowSettings(true)} style={{ padding: '5px 10px' }} title="Valuation settings">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2"/><path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.6 2.6l1.1 1.1M10.3 10.3l1.1 1.1M2.6 11.4l1.1-1.1M10.3 3.7l1.1-1.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
              </button>
            )}
            {company?.qb_connected && <span className="live-badge">Live · QBO</span>}
            <button className="btn-ghost" onClick={onLogout}>Sign out</button>
          </div>
        </div>
      </div>



      {/* Upload moved to topbar — see compact button below */}

      {/* Tab dashboard from uploaded template */}
      {templateData?.tabs?.length > 0 ? (
        <TabDashboard templateData={{ ...templateData, companyName: company?.name || slug, slug, industry: company?.industry || '' }} slug={slug} user={user} multiples={multiples} companySettings={companySettings} />
      ) : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)' }}>No dashboard data yet</div>
          <div style={{ fontSize: 12 }}>Open the upload panel above and upload the SCV Portfolio Upload Template to populate this dashboard</div>
        </div>
      )}

      {/* Settings panel */}
      {showSettings && user.role === 'scv_admin' && (
        <SettingsPanel slug={slug} onClose={() => setShowSettings(false)} onSave={s => { setCompanySettings(s); setShowSettings(false); }} />
      )}
    </div>
  );
}
